import { ref } from 'vue';
import type { Ref } from 'vue';

import { positionApi } from '@/entities/Position';
import { downloadBlobFile, handleApiError, useCustomToast, type SearchFilter } from '@/shared/lib';

type UsePositionsExportParams = {
  searchTerm: Ref<string>;
  sort: Ref<string>;
  order: Ref<string>;
  filter: Ref<SearchFilter>;
};

export const usePositionsExport = ({
  searchTerm,
  sort,
  order,
  filter,
}: UsePositionsExportParams) => {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const currentFilter = filter.value;
      const { blob, exportedCount, filename } = await positionApi.exportPositionsExcel({
        search: searchTerm.value.trim() || undefined,
        sort: sort.value || undefined,
        order: order.value || undefined,
        departmentId: currentFilter.departmentId ? Number(currentFilter.departmentId) : undefined,
        active:
          currentFilter.active !== undefined ? String(currentFilter.active) === 'true' : undefined,
        branchId: currentFilter.branchId ? Number(currentFilter.branchId) : undefined,
      });

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1 ? '1 cargo exportado' : `${exportedCount} cargos exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, 'Tente novamente.');
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExporting,
  };
};
